import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';

import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Subscription, Observable } from 'rxjs';
import {
  GeographicalZonesApiClientProvider,
  CountriesApiClientProvider,
  UpdateGeographicalZoneModel,
  CreateGeographicalZoneModel,
  defaultPaginationParameters,
  IPaginationResponse,
  ICountriesResponse,
  ErrorOrError,
  handleApiErrors,
  Logger,
  ICreateGeographicalZoneRequest
} from '../../../../../common';
import { ICreateGeographicalZoneResponse } from '../../../../../common/core/location-management/geographical-zones/dtos/responses/create-geographical-zone.response';
import { FormComponent, ToastService } from '../../../../shared/components';
import { FormInputConfig, ListValues } from '../../../../shared/interfaces/form-input-config';

@Component({
  selector: 'app-create-update-geographical-zone',
  imports: [ReactiveFormsModule, DialogModule, CommonModule, FormComponent, TranslateModule],
  templateUrl: './create-update-geographical-zone.component.html',
  styleUrl: './create-update-geographical-zone.component.scss'
})
export class CreateUpdateGeographicalZoneComponent {
  private client = inject(GeographicalZonesApiClientProvider).getClient();
  private clientCountries = inject(CountriesApiClientProvider).getClient();
  private toastService = inject(ToastService);
  private cdr = inject(ChangeDetectorRef);
  private dialogRef = inject(DynamicDialogRef);
  private translate = inject(TranslateService);
  modalData = inject(DynamicDialogConfig);
  private subscriptions: Subscription[] = [];
  formConfigs: FormInputConfig[] = [];
  isLoadingAddEditItem = false;
  itemsFormValidationEmit = false;
  validationErrors = {};
  itemId: string = '';
  model: UpdateGeographicalZoneModel = CreateGeographicalZoneModel.createDefault();
  countries: ListValues[] = [];

  ngOnInit(): void {
    this.itemId = this.modalData?.data?.item?.id ?? '';
    this.initializeItemData();
    this.getCountries();
  }
  private initializeItemData(): void {
    const item = this.modalData?.data?.item;
    if (item) {
      this.model = UpdateGeographicalZoneModel.createFromItem(item);
    }
  }
  private initForm(): void {
    this.formConfigs = this.model.getFormConfig(this.countries, this.modalData?.data?.visible, this.translate);
  }

  getCountries(keyword?: string) {
    this.clientCountries.get(defaultPaginationParameters).subscribe({
      next: (res: IPaginationResponse<ICountriesResponse>) => {
        this.processCountriesListResponse(res), this.initForm();
      },
      error: (errors: ErrorOrError[]) => handleApiErrors(errors, this.toastService)
    });
  }
  private processCountriesListResponse(response: IPaginationResponse<ICountriesResponse>): void {
    const items: ICountriesResponse[] = response.items ?? [];
    this.countries = items.map((item: ICountriesResponse) => ({
      label: item.englishName,
      value: item.id
    }));
  }

  formValuesChanged(data: any): void {
    Logger.debug('formValuesChanged:', data);
  }
  async submitForm(request: ICreateGeographicalZoneRequest): Promise<void> {
    Logger.debug('Submit form:', request, this.model);
    this.isLoadingAddEditItem = true;
    this.cdr.detectChanges();
    const request$: Observable<void | ICreateGeographicalZoneResponse> = this.model.id
      ? this.client.update(this.itemId, request)
      : this.client.create(request);
    request$.subscribe({
      next: (response) => this.handleActionSuccess(response),
      error: (errors) => this.handleActionError(errors),
      complete: () => this.finalizeAction()
    });
  }
  private handleActionSuccess(response: ICreateGeographicalZoneResponse | void): void {
    const successTitle = this.translate.instant('GENERAL.SUCCESS');
    const message = response
      ? this.translate.instant('GENERAL.SUCCESSFULLY_CREATED')
      : this.translate.instant('GENERAL.SUCCESSFULLY_UPDATED');
    this.toastService.showSuccess(successTitle, message);
    this.cdr.detectChanges();
    this.onClose(response ?? true);
  }
  private handleActionError(errors: ErrorOrError[]): void {
    Logger.debug('Action Error:', errors);
    if (this.toastService) {
      handleApiErrors(errors, this.toastService);
    }
    this.isLoadingAddEditItem = false;
  }
  private finalizeAction(): void {
    this.isLoadingAddEditItem = false;
    this.itemsFormValidationEmit = false;
  }
  emitValidation(): void {
    this.itemsFormValidationEmit = !this.itemsFormValidationEmit;
    this.cdr.detectChanges();
  }
  onClose(data?: boolean | ICreateGeographicalZoneResponse): void {
    this.dialogRef.close(data);
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
  }
}
